import React, { useMemo } from 'react'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { HighlightCell } from 'components/Shared/MuiTable/Cell/HighlightCell'
import { PLATFORM_ENUM } from 'services/api/constants'
import { CampaignState } from 'state/campaigns'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { GridColDefProps } from 'components/Submissions/Review/Submissions/SimpleTable/constants'
import { useNewTranslation } from 'hooks'
import { useSelector } from 'react-redux'
import { selectTasks } from 'state/tasks'
import _ from 'lodash'
import { MediaCell, MediaCellProps } from 'components/Shared/MuiTable/Cell/MediaCell'
import { TFunction } from 'i18next'
import { TopPerformersTab, UpdateAmbassadorFavoritePayload } from 'state/campaignReport'
import { GetTopPerformersResponse } from 'services/api/campaignReport'
import { convertToThousandSeparator } from 'components/utils'
import { Ambassador } from 'components/Shared/Permission/enums'
import { usePermission } from 'hooks/permissions'
import { PartiposterProfile, SocialMediaAccount } from 'state/submissions'

export interface TopPerformersRowProps {
    id: string
    uuid: string
    favorite: boolean
    rank: {
        value: number
    }
    profile: {
        profile: PartiposterProfile
    }
    socialMediaInfo: {
        profiles: (SocialMediaAccount | undefined)[]
    }
    submissions: []
    performanceMetric: {
        value: string
    }
}

const performanceMetricLabelMap = (t: TFunction) => ({
    most_engagement: t('submission.campaignReport.metric.totalEngagement'),
    highest_engagement_rate: t('submission.table.engagementRateLabel'),
    highest_content_engagement_rate: t('submission.table.contentEngagementRateLabel'),
    most_views: t('submission.table.viewsLabel'),
})

export const getTopPerformersColumnsDef = (
    campaign: CampaignState,
    selectedMetric: TopPerformersTab,
    onFavourite: (payload: UpdateAmbassadorFavoritePayload) => void,
    updateViewedRow?: (id: string) => void,
): GridColDefProps[] => {
    const [t] = useNewTranslation()
    const performanceMetricLabel = performanceMetricLabelMap(t)
    const tasks = useSelector(selectTasks)
    const ambassadorPermission = usePermission(Ambassador.AddRemoveFavourite)
    const campaignPlatforms = useMemo(() => {
        return _.uniq(tasks.map(task => task.platform))
    }, [tasks])

    if (!campaign) return []

    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return campaignPlatforms.includes(platform)
    }

    const cols: GridColDefProps[] = [
        ...(ambassadorPermission
            ? [
                  {
                      field: 'favorite',
                      headerName: t('submission.table.favourite'),
                      width: 60,
                      resizable: false,
                      renderCell: function Highlight(params: GridRenderCellParams): React.ReactElement {
                          return (
                              <HighlightCell
                                  {...params}
                                  icon="star"
                                  indicatorColor={params.row.favorite ? 'yolkYellow' : undefined}
                                  onClick={() => {
                                      onFavourite({
                                          favorited: params.row.favorite,
                                          partiposter_uuid: params.row.profile.profile.uuid,
                                          brand_uuid: campaign?.brand.uuid,
                                          brand_name: campaign?.brand.name,
                                      })
                                  }}
                                  isDisabled={false}
                              />
                          )
                      },
                      sortable: false,
                  },
              ]
            : []),
        {
            field: 'rank',
            headerName: t('submission.table.rank'),
            align: 'center',
            width: 60,
            resizable: false,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        },
        {
            field: 'profile',
            headerName: t('submission.table.profile'),
            headerClassName: 'align-left-header',
            width: 160,
            resizable: false,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: t('submission.table.ageLabel'),
                },
            ],
        },
        {
            field: 'socialMediaInfo',
            headerName: t('submission.table.socialMediaInfo'),
            headerClassName: 'align-left-header',
            width: 180,
            resizable: false,
            sortable: false,
            renderCell: (props: GridRenderCellParams): React.ReactElement => {
                const handleUpdateViewed = () => {
                    if (updateViewedRow) {
                        updateViewedRow(props.row.uuid)
                    }
                }
                return <SocialMediaInfoCell {...props} onClick={handleUpdateViewed} />
            },
            subFields: [
                ...(hasPlatform(PLATFORM_ENUM.IG)
                    ? [
                          {
                              field: FIELDS_TYPE.IG_FOLLOWERS,
                              headerName: t('submission.table.instagramFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.IG_USERNAME,
                              headerName: t('submission.table.instagramUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.FB)
                    ? [
                          {
                              field: FIELDS_TYPE.FB_FRIENDS,
                              headerName: t('submission.table.facebookFriendsLabel'),
                          },
                          {
                              field: FIELDS_TYPE.FB_USERNAME,
                              headerName: t('submission.table.facebookUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                    ? [
                          {
                              field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                              headerName: t('submission.table.tikTokFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TIKTOK_USERNAME,
                              headerName: t('submission.table.tikTokUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                    ? [
                          {
                              field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                              headerName: t('submission.table.twitterFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TWITTER_USERNAME,
                              headerName: t('submission.table.twitterUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.XIAOHONGSHU)
                    ? [
                          {
                              field: FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS,
                              headerName: t('submission.table.XHSFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                              headerName: t('submission.table.XHSUsernameLabel'),
                          },
                      ]
                    : []),
            ],
        },
        {
            field: 'submissions',
            headerName: t('submission.table.postheader'),
            align: 'center',
            minWidth: 120,
            renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
            sortable: false,
            filterable: false,
            flex: 1,
        },
        {
            field: 'performanceMetric',
            align: 'center',
            headerName: performanceMetricLabel[selectedMetric],
            minWidth: 120,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            flex: 1,
        },
    ]

    return cols
}

export const getTopPerformersRowsDef = (data: GetTopPerformersResponse, selectedTab: TopPerformersTab) => {
    return data.tasks.map(task => {
        const transformedRanking = task.ranking.map((rank, index) => {
            const { partiposter_profile, files, value, favorite } = rank
            const { social_media_accounts: socialMediaAccounts } = partiposter_profile

            const igProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.IG)
            const fbProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.FB)
            const tiktokProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TIKTOK)
            const twitterProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TWITTER)
            const xiaohongshuProfile = socialMediaAccounts.find(
                account => account.platform === PLATFORM_ENUM.XIAOHONGSHU,
            )
            return {
                id: partiposter_profile.uuid,
                uuid: partiposter_profile.uuid,
                favorite: favorite,
                rank: { value: index + 1 },
                profile: { profile: partiposter_profile },
                socialMediaInfo: {
                    profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile, xiaohongshuProfile],
                },
                submissions: files,
                performanceMetric: {
                    value:
                        selectedTab === 'highest_content_engagement_rate' || selectedTab === 'highest_engagement_rate'
                            ? `${convertToThousandSeparator(value)}%`
                            : convertToThousandSeparator(value),
                },
            }
        })
        return {
            title: task.title,
            total_users: task.total_users,
            ranking: transformedRanking,
        }
    })
}
